import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, X, CheckCircle2, Circle } from "lucide-react";

const SEAL = "var(--seal)";
const INK = "var(--ink)";
const INK_55 = "var(--ink-55)";
const INK_72 = "var(--ink-72)";

/**
 * DemoChecklist
 * Lightweight, step-by-step "what to try" guide for first-time users.
 * - Visible only while the user has fewer than `THRESHOLD` user-completed steps
 * - Each step is dismissed locally (per-user) — never blocks the rest of the dashboard
 */
const STEPS = [
  {
    id: "explore_idea",
    label: "Click any idea card to see how editing works",
    sub: "Open Pipeline → tap a card → see the full idea drawer.",
    to: "/pipeline",
    cta: "Open Pipeline",
  },
  {
    id: "write_script",
    label: "Generate an AI script in 5 seconds",
    sub: "Dashboard → Write a script → pick platform → Generate.",
    actionTestId: "dashboard-open-script",
    cta: "Try it",
  },
  {
    id: "check_analytics",
    label: "See your growth chart",
    sub: "Sample data is already loaded — see how your trend would look.",
    to: "/analytics",
    cta: "Open Analytics",
  },
  {
    id: "swipe_inspirations",
    label: "Browse the Swipe File",
    sub: "Inspiration cards from MrBeast, Ali Abdaal, Casey Neistat — already pinned.",
    to: "/swipe",
    cta: "Open Swipe File",
  },
  {
    id: "set_goal",
    label: "Set or tweak a monthly goal",
    sub: "A quiet number, your terms — PintaFlow keeps score.",
    to: "/goals",
    cta: "Open Goals",
  },
];

const STORAGE_KEY = (uid) => `pintaflow:demoChecklist:${uid}`;

export default function DemoChecklist({ user, onTriggerScript }) {
  const [done, setDone] = useState(() => new Set());
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user?.user_id) return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY(user.user_id));
      if (raw) {
        const parsed = JSON.parse(raw);
        setDone(new Set(parsed.done || []));
        setDismissed(!!parsed.dismissed);
      }
    } catch { /* noop */ }
  }, [user?.user_id]);

  const persist = (next) => {
    if (!user?.user_id) return;
    try {
      localStorage.setItem(
        STORAGE_KEY(user.user_id),
        JSON.stringify({ done: [...next.done], dismissed: !!next.dismissed })
      );
    } catch { /* noop */ }
  };

  // Show only for demo / guest contexts OR when explicit unfinished items remain.
  // Heuristic: show if user is guest, OR user is onboarded and hasn't completed the checklist.
  const isGuest = !!user?.is_guest;
  const onboarded = !!user?.preferences?.onboarded;
  const allDone = done.size >= STEPS.length;
  if (dismissed || allDone) return null;
  if (!isGuest && !onboarded) return null; // first-time users go through Onboarding first

  const completeStep = (id) => {
    const next = new Set(done);
    next.add(id);
    setDone(next);
    persist({ done: next, dismissed });
  };

  const dismiss = () => {
    setDismissed(true);
    persist({ done, dismissed: true });
  };

  return (
    <div
      className="card mt-6 mb-2 relative overflow-hidden"
      style={{
        background: "linear-gradient(135deg, rgba(216,168,87,0.07), rgba(216,168,87,0.02) 60%)",
        border: "1px solid rgba(216,168,87,0.30)",
      }}
      data-testid="demo-checklist"
    >
      <button
        onClick={dismiss}
        className="absolute top-4 right-4 w-7 h-7 rounded-full flex items-center justify-center transition hover:opacity-90"
        style={{ color: INK_55 }}
        data-testid="demo-checklist-dismiss"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>

      <div className="eyebrow mb-1"><span className="seal-dot mr-2 align-middle" /> Try these next</div>
      <h3
        className="font-heading text-2xl md:text-3xl"
        style={{ fontVariationSettings: "'opsz' 64, 'wght' 450" }}
      >
        Five small <span className="font-display-italic">tours</span>.
      </h3>
      <p className="text-sm mt-2" style={{ color: INK_72 }}>
        Tap a step to see that part of the studio. The list remembers what you&apos;ve already tried.
      </p>

      <div className="mt-5 space-y-2">
        {STEPS.map((s, idx) => {
          const completed = done.has(s.id);
          const Body = (
            <div
              className="flex items-start gap-3 rounded-xl px-3 py-3 transition group"
              style={{
                background: completed ? "rgba(216,168,87,0.06)" : "transparent",
                border: `1px solid ${completed ? "rgba(216,168,87,0.30)" : "var(--ink-22)"}`,
              }}
            >
              <span className="mt-0.5 flex-shrink-0">
                {completed ? (
                  <CheckCircle2 size={16} style={{ color: SEAL }} strokeWidth={1.6} />
                ) : (
                  <Circle size={16} style={{ color: INK_55 }} strokeWidth={1.6} />
                )}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm" style={{ color: completed ? INK_55 : INK, textDecoration: completed ? "line-through" : "none" }}>
                  <span className="mr-2 font-mono text-[10px]" style={{ color: SEAL, opacity: 0.7 }}>
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  {s.label}
                </div>
                {!completed && (
                  <div className="text-[11px] mt-0.5" style={{ color: INK_55 }}>{s.sub}</div>
                )}
              </div>
              {!completed && (
                <span
                  className="text-[11px] inline-flex items-center gap-1 flex-shrink-0 ml-1 px-2 py-1 rounded-full transition group-hover:opacity-100"
                  style={{ color: SEAL, opacity: 0.85 }}
                >
                  {s.cta} <ArrowRight size={11} />
                </span>
              )}
            </div>
          );

          // Either render as Link (route nav) or as a button (in-page action)
          if (s.to) {
            return (
              <Link
                key={s.id}
                to={s.to}
                onClick={() => completeStep(s.id)}
                data-testid={`demo-step-${s.id}`}
                className="block"
              >
                {Body}
              </Link>
            );
          }
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => { completeStep(s.id); onTriggerScript?.(); }}
              data-testid={`demo-step-${s.id}`}
              className="w-full text-left"
            >
              {Body}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-between text-[11px]" style={{ color: INK_55 }}>
        <span>{done.size} / {STEPS.length} tried</span>
        {done.size > 0 && (
          <button onClick={dismiss} className="underline underline-offset-2 hover:opacity-80" data-testid="demo-checklist-finish">
            I&apos;m good — hide this
          </button>
        )}
      </div>
    </div>
  );
}
